import { 
    DataTableLabels, 
    FilterType, 
    TableCellInputType,
    allFilterMethods,
    filterMethodsByInputType
 } from "@components/data-table/types"
import { DataTableCellInput } from "@components/data-table/data-table-input-value";
import { useState, useRef, useEffect, ReactNode } from "react";
import { Popover, Box, Divider, Stack, Button } from "@mui/material";
import { getTableInputAnchorOrigin, getTableInputTransformOrigin } from "@components/data-table/utils";
import { OutlinedSelect } from "@components/outlined-select";
import CurrentFiltersList from "@components/data-table/filter-input/current-filters-list";

export const TableFilterInput = ({
    onClose,
    anchor,
    keyOptions,
    keyLabels,
    filters,
    tableColumns,
    tableConfig,
    addFilter,
    removeFilter,
    removeAllFilters,
    filtersToRemove,
    setFiltersToRemove,
    getOptionsFormSelectFieldConfig,
    children
}: {
    onClose: () => void;
    anchor: HTMLElement | null;
    keyOptions: string[] | null;
    keyLabels: DataTableLabels;
    filters: any[];
    tableColumns: any[];
    tableConfig: Record<string, any>;
    addFilter: (filter: any) => void;
    removeFilter: (filters: any[]) => void;
    removeAllFilters: () => void;
    filtersToRemove: any[];
    setFiltersToRemove: (filters: any) => void;
    getOptionsFormSelectFieldConfig: (config: any) => any;
    children?: ReactNode;
}) => {
    const [filterKey, setFilterKey] = useState("");
    const [filterType, setFilterType] = useState<FilterType>(FilterType.Contains);
    const [filterValue, setFilterValue ] = useState<string | null>(null);
    const [filterEndValue, setFilterEndValue] = useState<string | null>(null);

    const currentColumn = tableColumns?.find((col) => col.id === filterKey);
    const currentConfig = tableConfig?.[filterKey];

    const currentInputType = currentColumn?.displayType as TableCellInputType || currentColumn?.inputType as TableCellInputType;
    const currentFilterMethods = 
        filterMethodsByInputType?.[currentInputType] || allFilterMethods;
    const selectOptions = getOptionsFormSelectFieldConfig(currentConfig);

    const keyOptionsStore = useRef<string[] | null>(keyOptions);
    if (keyOptions !== keyOptionsStore.current) {
        keyOptionsStore.current = keyOptions;
    }
    const currentKeyOptions = keyOptionsStore.current;

    // const { toggleShowClosedTickets } = useTicketTableDispatch();
    // const { showClosed } = useTicketTableContext();

    const isValidInput = 
        filterKey !== "" &&
        (filterValue !== null ||
        (currentInputType === TableCellInputType.Date && (filterEndValue !== null || filterValue !== null)) ||
            filterType === FilterType.isSet ||
            filterType === FilterType.isNotSet);

    const doesFilterExist = filters.some((filter) => 
        filter.column === filterKey && 
        filter.type === filterType && 
        filter.value === filterValue
    );

    const isValid = isValidInput && !doesFilterExist;

    const minWidth = 180;

    const handleSubmit = () => {        
        addFilter({
            id: Date.now(),
            column: filterKey as keyof any,
            type: filterType,
            value: filterValue,
            endValue: filterEndValue && currentInputType === TableCellInputType.Date ? filterEndValue : undefined,
            inputType: currentInputType,
        });
        setFilterKey("");
        setFilterType(FilterType.Contains);
        setFilterValue("");
        setFilterEndValue("");
        onClose();
    }

    const handleUpdateFilters = () => {
        removeFilter(filtersToRemove);
        setFiltersToRemove([]);
        onClose();
    }

    useEffect(() => {
        if(!currentFilterMethods.includes(filterType)) {
            setFilterType(currentFilterMethods[0]);
            setFilterValue(null);
        }
    }, [currentInputType, currentFilterMethods, filterType])

    return (
        <Popover
            anchorOrigin={getTableInputAnchorOrigin()}
            transformOrigin={getTableInputTransformOrigin()}
            open={!!anchor}
            anchorEl={anchor}
            onClose={onClose}
            className="tickets-table-filter-input-popover"
            sx={{
                "& .MuiPaper-root": {
                    p: 1.5,
                },
            }}
        >
            <Box sx={{ mt: 0, mb: 0, pb: 1, pl: 0.5 }} component="p">
                New Filter:
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
                <OutlinedSelect
                    label="Select a Column"
                    inputRef={(inputRef) => filterKey === "" && inputRef?.focus()}
                    value={filterKey}
                    onChange={setFilterKey}
                    wrapperSx={{
                        minWidth,
                        maxWidth: {
                            xs: '100%',
                            md: '180px',
                        },
                    }}
                    options={currentKeyOptions?.map((key) => ({
                        value: key,
                        label: keyLabels[key],
                    }))}
                />
                <DataTableCellInput
                    currentInputType={currentInputType}
                    value={filterValue}
                    setValue={setFilterValue}
                    minWidth={minWidth}
                    filterType={filterType}
                    options={selectOptions as any}
                    endValue={filterEndValue}
                    setEndValue={setFilterEndValue}
                />
                <Button disabled={!isValid} variant="contained" onClick={handleSubmit}>
                    Add Filter
                </Button>
            </Stack>
            {/* <Box sx={{ pl: 0.5 }}>
                <Stack direction="row" alignItems="center">
                    <Switch
                        checked={showClosed}
                        onChange={handleShowClosedToggle}
                        size="small"
                        sx={{ ml: 1 }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Show Closed Tickets
                    </Typography>
                </Stack>
            </Box> */}
            { children }
            <CurrentFiltersList
                filters={filters}
                filtersToRemove={filtersToRemove}
                removeAllFilters={removeAllFilters}
                handleUpdateFilters={handleUpdateFilters}
                setFiltersToRemove={setFiltersToRemove}
                keyLabels={keyLabels}
                tableCellInputConfig={tableConfig}
            />
        </Popover>
    );
};

export default TableFilterInput;